
/**
 * Extracts the English text from a multilingual text object
 * @param textObj The multilingual text object from TED API
 * @param fallback Fallback text if English version is not available
 * @returns The English text or fallback value
 */
export const getEnglishText = (textObj: any, fallback: string = "No title available"): string => {
  if (!textObj) return fallback;
  
  // If it's a string, return it directly
  if (typeof textObj === 'string') return textObj;
  
  // If it's an array, return the first item
  if (Array.isArray(textObj)) return textObj[0] || fallback;
  
  // If it has an 'eng' key, use that (English)
  if (textObj.eng) {
    const engText = Array.isArray(textObj.eng) ? textObj.eng[0] : textObj.eng;
    if (engText) return engText;
  }
  
  // Try Danish if English is not available
  if (textObj.dan) {
    const danText = Array.isArray(textObj.dan) ? textObj.dan[0] : textObj.dan;
    if (danText) return danText;
  }
  
  // Try other languages if English and Danish are not available
  const languages = Object.keys(textObj);
  if (languages.length > 0) {
    const firstLang = languages[0];
    const langText = Array.isArray(textObj[firstLang]) ? textObj[firstLang][0] : textObj[firstLang];
    if (langText) return langText;
  }
  
  return fallback;
};

/**
 * Extracts the Danish text from a multilingual text object
 * @param textObj The multilingual text object from TED API
 * @param fallback Fallback text if Danish version is not available
 * @returns The Danish text or fallback value
 */
export const getDanishText = (textObj: any, fallback: string = "Ingen titel tilgængelig"): string => {
  if (!textObj) return fallback;
  
  // If it's a string, return it directly
  if (typeof textObj === 'string') return textObj;
  
  // If it's an array, return the first item
  if (Array.isArray(textObj)) return textObj[0] || fallback;
  
  // If it has a 'dan' key, use that (Danish)
  if (textObj.dan) {
    const danText = Array.isArray(textObj.dan) ? textObj.dan[0] : textObj.dan;
    if (danText) return danText;
  }
  
  // Try English if Danish is not available
  if (textObj.eng) {
    const engText = Array.isArray(textObj.eng) ? textObj.eng[0] : textObj.eng;
    if (engText) return engText;
  }
  
  // Try other languages if Danish and English are not available
  const languages = Object.keys(textObj);
  if (languages.length > 0) {
    const firstLang = languages[0];
    const langText = Array.isArray(textObj[firstLang]) ? textObj[firstLang][0] : textObj[firstLang];
    if (langText) return langText;
  }
  
  return fallback;
};

/**
 * Maps match reason keys to human-readable names in Danish
 * @param reasonKey The key for the match reason
 * @returns Human-readable name in Danish
 */
export const getMatchReasonName = (reasonKey: string): string => {
  const reasonMap: Record<string, string> = {
    'classification-cpv': 'Branchekode',
    'place-of-performance-country-lot': 'Land',
    'notice-title': 'Titel',
    'description-lot': 'Beskrivelse',
    'buyer-name': 'Organisation',
    'total-value': 'Værdi',
    'deadline-receipt-tender-date-lot': 'Deadline',
    'procedure-type': 'Procedure type'
  };
  
  return reasonMap[reasonKey] || reasonKey;
};

/**
 * Safely extracts the value from a potentially nested or array field
 * @param value The field value which could be nested, an array, or a simple value
 * @param defaultValue Default value to return if extraction fails
 * @returns Extracted value as a string
 */
export const safeExtractValue = (value: any, defaultValue: string = "Ikke specificeret"): string => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  
  // Handle direct string
  if (typeof value === 'string') {
    return value;
  }
  
  // Handle array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return defaultValue;
    }
    return safeExtractValue(value[0], defaultValue);
  }
  
  // Handle object with language keys
  if (typeof value === 'object') {
    // Try language-specific fields
    if (value.dan) {
      return safeExtractValue(value.dan, defaultValue);
    }
    
    if (value.eng) {
      return safeExtractValue(value.eng, defaultValue);
    }
    
    // Try the first available key
    const keys = Object.keys(value);
    if (keys.length > 0) {
      return safeExtractValue(value[keys[0]], defaultValue);
    }
  }
  
  return defaultValue;
};
